import { useState } from 'react';
import { Lock, User, Eye, EyeOff, Shield } from 'lucide-react';
import { loginAdmin } from '../../services/firebase';

interface AdminLoginProps {
  onLogin: (credentials: { username: string; password: string }) => void;
}

export function AdminLogin({ onLogin }: AdminLoginProps) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Try Firebase authentication
      console.log('Attempting Firebase login with:', credentials.username);
      await loginAdmin(credentials.username, credentials.password);
      console.log('Firebase login successful!');
      onLogin(credentials);
    } catch (firebaseError: any) {
      console.error('Firebase auth error:', firebaseError);
      
      // Fallback to simple authentication for backward compatibility
      if (credentials.username === 'admin' && credentials.password === 'admin123') {
        console.log('Using fallback authentication');
        onLogin(credentials);
      } else {
        setError(`Login failed: ${firebaseError.message || 'Invalid credentials'}. Try: admin@askgrandgajuwaka.com`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-gradient flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-luxury-pattern opacity-20"></div>
      
      <div className="relative max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-10">
          <div className="w-24 h-24 bg-gold-gradient rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-gold-xl">
            <Shield className="w-12 h-12 text-navy-900" />
          </div>
          <h1 className="text-4xl font-bold text-gold-400 mb-3">Hotel A.S.K Grand</h1>
          <p className="text-cream-200 text-lg">Luxury Admin Dashboard</p>
        </div>

        {/* Login Form */}
        <div className="glass-luxury rounded-3xl shadow-luxury-xl p-10 border border-gold-400/20">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-red-500/20 border border-red-400/30 text-red-300 px-5 py-4 rounded-xl text-sm backdrop-blur-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-bold text-gold-400 mb-3">
                Admin Username
              </label>
              <div className="relative">
                <User className="absolute left-4 top-4 w-5 h-5 text-gold-400" />
                <input
                  type="text"
                  id="username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full pl-12 pr-5 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300 backdrop-blur-sm"
                  placeholder="Enter admin username"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-gold-400 mb-3">
                Admin Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-4 w-5 h-5 text-gold-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full pl-12 pr-14 py-4 bg-white/10 border-2 border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-400 transition-colors text-white placeholder-cream-300 backdrop-blur-sm"
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-4 text-gold-400 hover:text-gold-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-5 rounded-2xl font-bold transition-all duration-300 text-lg ${
                isLoading
                  ? 'bg-cream-400 text-navy-900 cursor-not-allowed'
                  : 'bg-gold-gradient text-navy-900 hover:shadow-gold-xl transform hover:scale-105 pulse-luxury'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-2 border-navy-900 border-t-transparent rounded-full animate-spin"></div>
                  <span>Accessing Luxury Dashboard...</span>
                </div>
              ) : (
                'Access Admin Dashboard'
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-8 p-6 glass-navy rounded-2xl border border-navy-400/30">
            <h4 className="text-sm font-bold text-gold-400 mb-3">Demo Access Credentials:</h4>
            <div className="text-sm text-cream-200 space-y-2">
              <p><strong className="text-gold-400">Username:</strong> admin</p>
              <p><strong className="text-gold-400">Password:</strong> admin123</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-10">
          <p className="text-cream-300 text-sm">
            © 2024 Hotel A.S.K Grand. Luxury Hotel Management System.
          </p>
        </div>
      </div>
    </div>
  );
}